import React from 'react';

import { StageDefinition } from '../types';
import { lerp } from '../utils';

/**
 * rAF-based progress interpolation between startPercent and endPercent over durationMs.
 * Returns a number 0-100 representing overall progress.
 */
export function useProgressSimulation(
  stage: StageDefinition | null,
  active: boolean,
): number {
  const [progress, setProgress] = React.useState(stage?.startPercent ?? 0);
  const rafRef = React.useRef<number>(0);
  const startTimeRef = React.useRef<number>(0);

  React.useEffect(() => {
    if (!active || !stage) return;

    setProgress(stage.startPercent);
    startTimeRef.current = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTimeRef.current;
      const t = Math.min(elapsed / stage.durationMs, 1);
      const value = lerp(stage.startPercent, stage.endPercent, t);

      setProgress(value);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [stage, active]);

  return progress;
}
